import React, { useState, useMemo } from 'react';

import {
  DateInputWithConfirm,
  AutocompleteInputWithConfirm,
  InputWithConfirm,
  getStringFromDate,
  getDateFromString,
  ContinueButton,
  isValidDate,
} from 'shared-components';
import { countries as countriesJSON } from 'platform-data';
import { VQ } from '../../../../../store/student/types';

import { IDate, IFamilyMember } from '../../../../../types';

interface IProps {
  index: number;

  nationality: string;
  familyName: string;
  givenName: string;
  dob: string;

  onSaveAndClose: (details: IFamilyMember) => void;
}

const FamilyMembers = (props: IProps) => {
  const { index, onSaveAndClose } = props;
  const [nationality, setNationality] = useState(props.nationality);
  const [familyName, setFamilyName] = useState(props.familyName);
  const [givenName, setGivenName] = useState(props.givenName);
  const [dDob, setDDob] = useState(() => getDateFromString(props.dob));
  const [dDobError, setDDobError] = useState('');

  const nationalities = useMemo(() => [...new Set(countriesJSON.map(n => n.nationality))], []);
  const [nationalitySuggestions, setNationalitySuggestions] = useState(nationalities);

  const saveAndClose = () => {
    onSaveAndClose({
      nationality,
      familyName,
      givenName,
      dob: getStringFromDate(dDob),
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    saveAndClose();
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      }
      return '';
    }

    return '';
  };

  const onNationalityChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = nationalities.filter(s => s.toLowerCase().includes(value.toLowerCase()));
    setNationality(value);
    setNationalitySuggestions(suggestions);
  };

  const onBlurNationality = (evt: any) => {
    const value = (evt.currentTarget.value || '').trim();
    const nationality = nationalities.includes(value) ? value : '';
    setNationality(nationality);
    setNationalitySuggestions(nationalities);
  };

  const complete =
    nationality !== '' &&
    familyName !== '' &&
    givenName !== '' &&
    dDob.YYYY !== null &&
    dDob.MM !== null &&
    dDob.DD !== null &&
    dDobError === '';

  return (
    <>
      <AutocompleteInputWithConfirm
        key={`${VQ.NATIONALITY}-${index}`}
        id={`${VQ.NATIONALITY}-${index}`}
        value={nationality}
        label={`Nationality`}
        onChange={(evt: any) => onNationalityChange(evt)}
        suggestions={nationalitySuggestions}
        onBlur={(evt: any) => onBlurNationality(evt)}
      />

      <InputWithConfirm
        key={`${VQ.FAMILY_NAME}-${index}`}
        id={`${VQ.FAMILY_NAME}-${index}`}
        value={familyName}
        label={`Family name`}
        onChange={(evt: any) => {
          setFamilyName(evt.currentTarget.value);
        }}
      />

      <InputWithConfirm
        key={`${VQ.GIVEN_NAME}-${index}`}
        id={`${VQ.GIVEN_NAME}-${index}`}
        value={givenName}
        label={`Given name`}
        onChange={(evt: any) => {
          setGivenName(evt.currentTarget.value);
        }}
      />

      <DateInputWithConfirm
        key={`${VQ.DOB}-${index}`}
        id={`${VQ.DOB}-${index}`}
        label={`Date of birth`}
        value={dDob}
        errorText={dDobError}
        onChangeDD={(evt: any) => setDDob({ ...dDob, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDDob({ ...dDob, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDDob({ ...dDob, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDDobError(validateDate(dDob))}
      />

      <ContinueButton index={index} complete={complete} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} />
    </>
  );
};

export default FamilyMembers;
